import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { api } from "../lib/api";
import { createEngine, emptyState } from "../lib/engine";
import { useMapGestures } from "../lib/useMapGestures";
import { useRadioCtx } from "../lib/RadioContext";
import { TopBar, SKY_CYCLE } from "../components/kota/TopBar";
import { SideDock } from "../components/kota/SideDock";
import { ToaSheet } from "../components/kota/ToaSheet";
import { usePesanBaru } from "../lib/usePesanBaru";
import { BuildingSheet } from "../components/kota/BuildingSheet";
import { BottomDock } from "../components/kota/BottomDock";
import { RadioPlayer } from "../components/kota/RadioPlayer";
import { SearchSheet } from "../components/kota/SearchSheet";
import { KoranSheet } from "../components/kota/KoranSheet";
import { InfoSheet } from "../components/kota/InfoSheet";
import { PesanSheet } from "../components/kota/PesanSheet";
import { DompetSheet } from "../components/kota/DompetSheet";
import { TasSheet } from "../components/kota/TasSheet";
import { SuratSheet } from "../components/kota/SuratSheet";
import { KabarSheet } from "../components/kota/KabarSheet";
import { MisiSheet } from "../components/kota/MisiSheet";
import { MisiPill } from "../components/kota/MisiUI";
import { PilBalaiKota } from "../components/kota/PilBalaiKota";
import { useLencanaDock } from "../lib/useLencanaDock";
import { HintChip } from "../components/kota/HintChip";
import { FONT, DOCK_H, glassVars } from "../components/kota/ui";
import { pingKunjungan, useWargaAktif } from "../lib/presence";

const FIT_INSET = { top: 64, bottom: DOCK_H + 16 }; // ruang bar atas & dock bawah saat pas-layar

// Kota — halaman publik HP-first: peta satu layar penuh + dock gaya Thaloria (Radio, Koran, Pesan Warga, Info, Cari).
export default function Kota() {
  const { id } = useParams();
  useWargaAktif("kota");                                  // detak warga online (dashboard admin)
  useEffect(() => { pingKunjungan(id); }, [id]);          // pengunjung unik harian
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [state, setState] = useState(null);
  const [defs, setDefs] = useState([]);
  const [info, setInfo] = useState(null);
  const [skyMode, setSkyMode] = useState("real");   // real = mode malam otomatis (ikut jam nyata WIB); ketuk pil jam untuk Siang/Malam/Hujan
  const [sheet, setSheet] = useState(() => sp.get("buka") || null); // cari | koran | info | pesan | dompet | ... | null (?buka= dari halaman Kerja)
  const keKerja = () => nav(`/kerja?kota=${id}`);
  const radio = useRadioCtx();

  useEffect(() => {
    Promise.all([api.publicCity(id), api.catalog()])
      .then(([c, cat]) => { setState(c.state || emptyState()); setDefs(cat.buildings || []); })
      .catch(() => setState(emptyState()));
  }, [id]);

  // Gedung konstruksi yang selesai saat peta sedang dibuka → muat ulang state (server menggantinya dengan bangunan lanjutan)
  useEffect(() => {
    const akhir = (state?.buildings || []).map(b => b.konstruksi?.selesai_at && new Date(b.konstruksi.selesai_at).getTime()).filter(t => t && t > Date.now());
    if (!akhir.length) return;
    const t = setTimeout(() => api.publicCity(id).then(c => setState(c.state || emptyState())).catch(() => {}), Math.min(...akhir) - Date.now() + 1500);
    return () => clearTimeout(t);
  }, [state, id]);

  useEffect(() => {
    if (!state || !canvasRef.current) return;
    const E = createEngine(canvasRef.current);
    E.setHud(false);     // jam kota tampil sebagai pil React (TopBar)
    E.setDebugFps(new URLSearchParams(window.location.search).has("fps")); // ?fps=1 → overlay diagnosa performa HP
    E.setSkyMode("real"); // langit & jam kota publik ikut jam nyata WIB
    // Kamera awal: LANGSUNG zoom ke pusat kota (bukan pulau utuh) — pusat = titik berat tapak bangunan
    // non-Alam (kawasan terpadat); kota kosong → tengah pulau.
    const W = canvasRef.current.clientWidth, H = canvasRef.current.clientHeight;
    const alam = new Set(defs.filter(d => d.category === "Alam").map(d => d.bid));
    let sx = 0, sy = 0, sw = 0;
    for (const b of state.buildings || []) {
      if (alam.has(b.bid)) continue;
      const d = defs.find(x => x.bid === b.bid), w = d?.w || 1, h = d?.h || 1, a = w * h;
      sx += (b.x + w / 2) * a; sy += (b.y + h / 2) * a; sw += a;
    }
    E.cam.zoom = W < 600 ? 0.3 : 0.42;   // ±8 blok terlihat melebar di HP (sesuai referensi user), bukan close-up
    E.centerOn(sw ? sx / sw : 30, sw ? sy / sw : 30, W / 2, FIT_INSET.top + (H - FIT_INSET.top - FIT_INSET.bottom) / 2);
    E.render(state, defs);
    engineRef.current = E;
  }, [state, defs]);

  const toggleSky = () => { const m = SKY_CYCLE[skyMode] || "real"; setSkyMode(m); engineRef.current?.setSkyMode(m); };
  // Kaca menggelap lembut mengikuti atmosfer engine (malam/hujan) — dibaca berkala, transisi CSS .8s
  const [malam, setMalam] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setMalam((engineRef.current?.dark || 0) > 0.45), 400);
    return () => clearInterval(t);
  }, []);
  const pesanBaru = usePesanBaru(sheet === "pesan");
  const token = localStorage.getItem("jawsel_warga_token");
  const lencana = useLencanaDock(token, sheet);
  // Misi harian: aksi sisi klien (koran dibuka, radio diputar) dicatat sekali per sesi; pil "Misi n/10" di bawah bar atas
  const aksiRef = useRef({});
  const aksiMisi = useCallback(key => {
    if (!token || aksiRef.current[key]) return;
    aksiRef.current[key] = 1;
    api.misiAksi(token, key).then(r => { (r.baru || []).forEach(j => toast.success(`Misi harian selesai: ${j}`)); lencana.segarkan(); }).catch(() => {});
  }, [token]); // eslint-disable-line
  useEffect(() => { if (sheet === "koran") aksiMisi("koran"); }, [sheet, aksiMisi]);
  useEffect(() => { if (radio.playing) aksiMisi("radio"); }, [radio.playing, aksiMisi]);
  const misiDilihatKey = `jawsel_misi_dilihat_${new Date().toLocaleDateString("id-ID")}`;
  const [misiBaru, setMisiBaru] = useState(() => !localStorage.getItem(misiDilihatKey));
  const bukaMisi = () => { localStorage.setItem(misiDilihatKey, "1"); setMisiBaru(false); openSheet("misi"); };
  // "Ke sana" dari Misi: rute halaman lain, sheet di peta (?buka=…), atau nyalakan radio
  const keSana = tujuan => {
    closeSheet();
    if (!tujuan) return;
    if (tujuan === "?buka=radio") { if (!radio.on) radio.toggle(); return; }
    if (tujuan.startsWith("?")) { nav(`/kota/${id}${tujuan}`); return; }
    if (tujuan === "/alun") { nav(`/alun/${id}`); return; }
    nav(tujuan === "/kerja" || tujuan === "/mall" ? `${tujuan}?kota=${id}` : tujuan);
  };
  // Jumlah warga yang sedang di Alun-alun kota ini (titik penanda di dock bila ada yang berkumpul), disegarkan tiap 30 dtk
  const [alunOnline, setAlunOnline] = useState(0);
  useEffect(() => {
    const cek = () => api.alunInfo(id).then(d => setAlunOnline(d.online || 0)).catch(() => {});
    cek(); const t = setInterval(cek, 30000);
    return () => clearInterval(t);
  }, [id]);

  const sizeOf = useCallback((b, def) => {
    let w = def?.w || 1, h = def?.h || 1;
    if ((b.rot || 0) % 2 === 1) [w, h] = [h, w];
    return [w, h];
  }, []);
  const openInfo = useCallback((b, def) => setInfo({ ...b, def, name: def?.name, desc: def?.desc, floors: def?.floors, category: def?.category, w: def?.w, h: def?.h }), []);

  // Ketuk peta → cari bangunan di tile itu
  const onTap = useCallback((lx, ly) => {
    const E = engineRef.current;
    if (!E || !state) return;
    const [gx, gy] = E.toGrid(lx, ly);
    const di = b => { const [bw, bh] = sizeOf(b, defs.find(d => d.bid === b.bid)); return gx >= b.x && gy >= b.y && gx < b.x + bw && gy < b.y + bh; };
    // billboard atap (def.atap) diutamakan agar kartunya bisa dibuka meski menumpang di gedung
    const b = (state.buildings || []).find(b => di(b) && defs.find(d => d.bid === b.bid)?.atap) || (state.buildings || []).find(di);
    if (b) openInfo(b, defs.find(d => d.bid === b.bid)); else setInfo(null);
  }, [state, defs, sizeOf, openInfo]);
  const gestures = useMapGestures(engineRef, onTap);

  // Statistik kota untuk baris status dock (aset alam & ruang terbuka taman/lapangan tidak dihitung — sama dengan dashboard admin)
  const nB = useMemo(() => {
    const bukan = new Set([...defs.filter(d => d.category === "Alam").map(d => d.bid), "taman", "lapangan"]);
    return (state?.buildings || []).filter(b => !bukan.has(b.bid)).length;
  }, [state, defs]);
  const nR = (state?.roads || []).length;
  const openSheet = key => { if (key === "kerja") return keKerja(); if (key === "alun") return nav(`/alun/${id}`); if (key === "rumahku") return nav(localStorage.getItem("jawsel_warga_token") ? "/rumah/saya" : `/profil?kota=${id}`); setInfo(null); setSheet(key); };
  const closeSheet = () => { setSheet(null); if (sp.get("buka")) setSp({}, { replace: true }); };
  // ?buka=<sheet>[&ke=<ig>] berubah saat halaman sudah terbuka (mis. dari Kabar → Surat) → buka sheet yang diminta
  useEffect(() => { const b = sp.get("buka"); if (b) { setInfo(null); setSheet(b); } }, [sp]);

  return (
    <div data-testid="kota-fullscreen" onPointerDownCapture={radio.onFirstTouch} data-malam={malam ? "1" : "0"}
      style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#cfe6f4", color: "#141414", fontFamily: FONT, ...glassVars(malam) }}>
      <canvas ref={canvasRef} data-testid="city-canvas" {...gestures}
        style={{ display: "block", width: "100%", height: "100%", cursor: "grab", touchAction: "none" }} />

      {!sheet && <PilBalaiKota engineRef={engineRef} state={state} defs={defs} cityId={id} terverifikasi={!!token} onClick={() => nav(`/profil?kota=${id}`)} />}
      <TopBar cityId={id} engineRef={engineRef} skyMode={skyMode} onSky={toggleSky} />
      <SideDock active={sheet} onOpen={openSheet} badge={lencana} />
      <MisiPill ringkas={lencana.misi} baru={misiBaru && !!token} onClick={bukaMisi} />
      <RadioPlayer radio={radio} mini={radio.mini} onMini={radio.setMini} />
      {!info && !sheet && <HintChip />}
      {info && !sheet && <BuildingSheet info={info} cityId={id} malam={malam} defs={defs} onClose={() => setInfo(null)} onMall={() => nav(`/mall?kota=${id}`)} onDealer={() => nav(`/dealer?kota=${id}`)} />}
      <BottomDock active={sheet} onOpen={openSheet} radio={radio} badge={{ pesan: pesanBaru, alun: alunOnline > 0 }} />

      {sheet === "cari" && <SearchSheet nB={nB} nR={nR} cityId={id} onWarga={w => nav(`/rumah/${w.ig_username}`)} onClose={closeSheet} />}
      {sheet === "koran" && <KoranSheet cityId={id} onClose={closeSheet} />}
      {sheet === "info" && <InfoSheet onClose={closeSheet} />}
      {sheet === "pesan" && <PesanSheet cityId={id} onClose={closeSheet} />}
      {sheet === "dompet" && <DompetSheet cityId={id} onClose={closeSheet} onKerja={keKerja} />}
      {sheet === "tas" && <TasSheet cityId={id} onClose={closeSheet} />}
      {sheet === "surat" && <SuratSheet cityId={id} onClose={closeSheet} igAwal={sp.get("ke")} />}
      {sheet === "kabar" && <KabarSheet cityId={id} onClose={closeSheet} />}
      {sheet === "toa" && <ToaSheet cityId={id} onClose={closeSheet} />}
      {sheet === "misi" && <MisiSheet cityId={id} onClose={closeSheet} onKeSana={keSana} />}
      <Toaster theme="light" position="top-center" />
    </div>
  );
}
